import useSWR from "swr";
import {Utils} from "@/common/utils";
import { StandardResponse } from "@/types/swr";
import { useMemo } from "react";

import fetcher from "@/common/fetcher";
import {
    deleteApiV1SuperAdminSchoolsBySchoolIdTermsByTermId,
    getApiV1SuperAdminSchoolsBySchoolIdTerms,
    postApiV1SuperAdminSchoolsBySchoolIdTerms,
    putApiV1SuperAdminSchoolsBySchoolIdTermsByTermId
} from "Services/platform/client";
import { isPlatformApiEnabled } from "Services/platform/module-flags";
import { enfluxTermToTermRequest, termDtoToEnfluxTerm } from "Services/platform/term-mappers";
import type { TermWriteFields } from "Services/platform/term-mappers";
import { EAIClient } from "Services/EAIClient";


const clientConfigs = Utils.getEAIClientConfigs();

export interface EnfluxTerm {
    id: number,
    school_id: string,
    name: string,
    term: string,
    term_type: string,
    calendar_year: string,
    term_order: number,
    acad_year: string,
    start_date: string,
    end_date: string,
    is_active?: boolean,
}

export interface TermsResponse {
    data: EnfluxTerm[]
}

/**
 * SWR cache key for the terms list. The flag is part of the key so flipping it
 * refetches rather than serving the other API's cached answer.
 */
type TermsKey = readonly ["terms", string, boolean];

/** Terms oldest-first by end date, the order the table has always rendered. */
function byEndDate(terms: EnfluxTerm[]): EnfluxTerm[] {
    return [...terms].sort(
        (firstTerm, secondTerm) =>
            new Date(firstTerm.end_date).getTime() - new Date(secondTerm.end_date).getTime()
    );
}

/**
 * Reads the school's terms from platform-api or the legacy API, depending on
 * the `platform_api / terms` flag. Both branches return the same `EnfluxTerm[]`,
 * so nothing above this function knows which API answered.
 *
 * The generated SDK resolves with an `error` payload instead of throwing, so a
 * failed request would otherwise look like an empty term list; hence the
 * explicit throw.
 */
async function fetchTerms(schoolId: string, usePlatformApi: boolean): Promise<EnfluxTerm[]> {
    if (usePlatformApi) {
        const {data, error} = await getApiV1SuperAdminSchoolsBySchoolIdTerms({path: {schoolId}});

        if (error) {
            throw error;
        }

        const paged = data?.data;
        const content = paged?.content ?? [];

        // The endpoint pages at 20 by default and its spec exposes no page or
        // size parameter (`query?: never`), so there is no way to ask for the
        // rest. Failing here is deliberate: a silently short list on a screen
        // used to edit and delete terms is worse than an error, and the flag
        // flips back in one step.
        if (paged?.totalElements != null && content.length < paged.totalElements) {
            throw new Error(
                `platform-api returned ${content.length} of ${paged.totalElements} terms. `
                + "The endpoint exposes no paging parameters, so the list would be truncated."
            );
        }

        return content.map(dto => termDtoToEnfluxTerm(dto, schoolId));
    }

    const response: TermsResponse = await fetcher(
        `${clientConfigs.endpoint}/super_admin/schools/${schoolId}/terms`,
        {}
    );

    return response.data ?? [];
}

export function useTerms() {
    // Build-time config, so this is settled before the first render and a page
    // load can never call both APIs while a flag resolves.
    const usePlatformApi = isPlatformApiEnabled("terms");
    const schoolId = clientConfigs.school;

    const key: TermsKey | null = schoolId ? ["terms", schoolId, usePlatformApi] : null;

    const {data, error, mutate} = useSWR<EnfluxTerm[], Error>(
        key,
        ([, school, viaPlatformApi]: TermsKey) => fetchTerms(school, viaPlatformApi)
    );

    return {
        data: data ? {data: byEndDate(data)} as TermsResponse : undefined,
        error,
        mutate
    };
}

/**
 * The school's active terms (EAI-5645), readable by any signed-in user — the
 * super-admin terms list above is not. Pass `enabled = false` to skip the
 * request, e.g. while the `ui / configurable_active_terms` flag is off.
 */
export function useActiveTerms(enabled = true) {
    const schoolId = clientConfigs.school;
    const {data, error} = useSWR<StandardResponse<EnfluxTerm[]>, Error>(
        enabled && schoolId ? `${clientConfigs.endpoint}/schools/${schoolId}/active-terms` : null,
        {revalidateOnFocus: false}
    );

    return {
        data: data?.data,
        error
    };
}

/**
 * Term writes, routed to platform-api or the legacy API by the same
 * `platform_api / terms` flag the read uses, so a school never writes to one
 * API while reading the other.
 *
 * Two differences from the legacy path drive the shape of these:
 *
 * - platform-api's PUT is a **full replace**, where the legacy PUT filters the
 *   body down to the fields it was sent. Every update here therefore merges its
 *   changes over the whole existing term. Sending a bare `{is_active}` the way
 *   the legacy toggle does would clear every other field.
 * - The generated SDK resolves with an `error` payload instead of throwing, so
 *   a failed write would otherwise look like a success and the table would
 *   refresh over unchanged data. Each write checks and throws, which is what
 *   the component's existing try/catch already expects.
 */

const eaiClient = new EAIClient();

function requireSchool(): string {
    const schoolId = clientConfigs.school;

    if (!schoolId) {
        throw new Error("No school selected; cannot write terms.");
    }

    return schoolId;
}

/** Creates a term. `fields` must be complete: platform-api requires every core field. */
export async function createTerm(fields: TermWriteFields): Promise<void> {
    const schoolId = requireSchool();

    if (!isPlatformApiEnabled("terms")) {
        await eaiClient.createEnfluxTerm(toLegacyTerm(fields));
        return;
    }

    const {error} = await postApiV1SuperAdminSchoolsBySchoolIdTerms({
        path: {schoolId},
        body: enfluxTermToTermRequest(fields)
    });

    if (error) {
        throw error;
    }
}

/**
 * Updates a term. `changes` is merged over `term` before sending, so the
 * platform-api full replace cannot drop fields the caller did not mean to touch.
 */
export async function updateTerm(term: EnfluxTerm, changes: Partial<TermWriteFields>): Promise<void> {
    const schoolId = requireSchool();

    if (!isPlatformApiEnabled("terms")) {
        // The legacy API filters the body down to its update_fields, so the
        // caller's changes are sent on their own, as they always have been.
        await eaiClient.updateEnfluxTerm(term.id, toLegacyTerm(changes));
        return;
    }

    const {error} = await putApiV1SuperAdminSchoolsBySchoolIdTermsByTermId({
        path: {schoolId, termId: term.id},
        body: enfluxTermToTermRequest({...term, ...changes})
    });

    if (error) {
        throw error;
    }
}

/**
 * Marks a term active or inactive (EAI-5644). On platform-api this is a full
 * replace of the term with only `is_active` changed.
 */
export function setTermActive(term: EnfluxTerm, isActive: boolean): Promise<void> {
    return updateTerm(term, {is_active: isActive});
}

/** Deletes a term. */
export async function deleteTerm(termId: number): Promise<void> {
    const schoolId = requireSchool();

    if (!isPlatformApiEnabled("terms")) {
        await eaiClient.deleteEnfluxTerm(termId);
        return;
    }

    const {error} = await deleteApiV1SuperAdminSchoolsBySchoolIdTermsByTermId({
        path: {schoolId, termId}
    });

    if (error) {
        throw error;
    }
}

/** The legacy endpoints take the snake_case shape the UI already holds. */
function toLegacyTerm(fields: Partial<TermWriteFields>): Partial<EnfluxTerm> {
    return fields;
}

export interface PostscriptTermSource {
    name: string,
}

export function usePostscriptTerms() {
    const {data, error, mutate} = useSWR<StandardResponse<PostscriptTermSource[]>, Error>(`${clientConfigs.endpoint}/super_admin/schools/${clientConfigs.school}/postscript/sources`);
    return {
        data,
        error,
        mutate
    };
}

export function usePostscriptTermOptions() {
    const { data, error } = usePostscriptTerms();
    return useMemo(() => {
        return {
            options: data?.data.map((source) => ({
                value: source.name,
                label: source.name,
            })),
            error,
        };
    }, [data, error]);
}
